import React, { createContext, useState, useContext, useEffect } from 'react';
import Toast from '@components/Toast';
import { timers } from '@helpers/Constants';
import { ToastContextProps, ToastProviderProps } from '@helpers/Models';

// Define types for the context

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
}): JSX.Element => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToastMessage = (message: string, type: 'success' | 'error') => {
    // Reset first so repeated socket messages still retrigger toast rendering.
    setShowToast(false);
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setShowToast(true);
    }, 0);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showToast) {
      // Hide toast after 4 seconds
      timer = setTimeout(() => {
        setShowToast(false);
      }, timers.TOAST_AUTO_CLOSE);
    }
    // Clean up the timer
    return () => clearTimeout(timer);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToastMessage }}>
      {children}
      {showToast && (
        <Toast
          title={toastType === 'success' ? 'Success' : 'Error'}
          message={toastMessage}
          onClose={handleCloseToast}
          type={toastType}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextProps => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
